import { useEffect, useState } from "react";
import { Sparkles, MapPin, Briefcase, Stethoscope, HandHelping, Hourglass } from "lucide-react";
import api from "../lib/api";

const TYPE_META = {
  "Medical Electives": {
    icon: Stethoscope,
    tone: "from-rose-50 to-pink-50 border-rose-200 text-rose-900",
    accent: "text-rose-700 bg-rose-100",
  },
  Internship: {
    icon: Briefcase,
    tone: "from-sky-50 to-indigo-50 border-sky-200 text-sky-900",
    accent: "text-sky-700 bg-sky-100",
  },
  "Volunteer Project": {
    icon: HandHelping,
    tone: "from-emerald-50 to-teal-50 border-emerald-200 text-emerald-900",
    accent: "text-emerald-700 bg-emerald-100",
  },
};

/**
 * ProjectOverviewPanel — AI-generated placement briefing on the
 * Participant Portal. Waits gracefully while PCWM finalises the
 * placement, then renders a warm paragraph + task list + type badge.
 */
export default function ProjectOverviewPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/participant/project-overview")
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) return null;

  if (!data?.ready) {
    return (
      <div className="aii-card p-5 border-l-4 border-amber-400 bg-amber-50/50" data-testid="project-overview-empty">
        <div className="flex items-start gap-3">
          <Hourglass size={18} className="text-amber-700 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold">Awaiting placement</div>
            <h3 className="font-display font-semibold text-lg text-amber-900 mt-0.5">Your project briefing is on the way</h3>
            <p className="text-sm text-amber-900/80 mt-1">
              {data?.reason || "Your coordinator is still finalising the placement — check back soon."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const meta = TYPE_META[data.placement_type] || TYPE_META["Volunteer Project"];
  const Icon = meta.icon;
  const location = [data.city, data.destination].filter(Boolean).join(", ");

  return (
    <div className={`aii-card p-5 bg-gradient-to-br border ${meta.tone}`} data-testid="project-overview-card">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${meta.accent}`}>
            <Icon size={16} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold flex items-center gap-1">
              <Sparkles size={11} /> Your placement briefing
            </div>
            <h3 className="font-display font-semibold text-lg mt-0.5" data-testid="project-overview-placement">
              {data.placement}
            </h3>
            <div className="text-xs opacity-80 flex items-center gap-1.5 mt-1 flex-wrap">
              {location && <span className="flex items-center gap-0.5"><MapPin size={10}/> {location}</span>}
              {data.programme && <span>· {data.programme}</span>}
              {data.start_date && <span>· {data.start_date}{data.end_date ? ` → ${data.end_date}` : ""}</span>}
            </div>
          </div>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${meta.accent}`}
          data-testid="project-overview-type"
        >
          {data.placement_type}
        </span>
      </div>

      {data.project_overview && (
        <p className="text-sm leading-relaxed mb-3" data-testid="project-overview-text">
          {data.project_overview}
        </p>
      )}

      {(data.volunteer_tasks || []).length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold mb-2">
            What you&rsquo;ll likely be doing
          </div>
          <ul className="space-y-1.5" data-testid="project-overview-tasks">
            {data.volunteer_tasks.map((t, i) => (
              <li key={`task-${i}-${t.slice(0, 24)}`} className="text-sm flex items-start gap-2">
                <span className={`mt-1 w-1.5 h-1.5 rounded-full ${meta.accent.split(" ")[1]}`}></span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="text-[10px] opacity-60 mt-3 pt-3 border-t border-current/10">
        AI-drafted from your PCWM&rsquo;s placement notes. If anything looks off, message your coordinator.
      </div>
    </div>
  );
}
